/**
 * Posse — o segundo eixo da estante, ortogonal ao ciclo de leitura.
 *
 * ## Por que isto existe
 *
 * `user_books.status` era um enum de QUATRO valores mutuamente exclusivos
 * (`want_to_read` · `reading` · `read` · `owned`), o que forçava o usuário a
 * escolher entre dizer o que faz com o livro e dizer se o livro é dele. Marcar
 * "Lendo" apagava a informação de posse; não havia como expressar "quero e já
 * tenho" nem "li e tenho". Para o caso que motivou a mudança — alguém entra no
 * seu perfil pra descobrir o que te dar de presente — o dado que importa é
 * exatamente o que não existia: **quero, e ainda NÃO tenho**.
 *
 * Agora são dois eixos independentes:
 *
 * | Eixo             | Campo       | Valores                                        |
 * |------------------|-------------|------------------------------------------------|
 * | Ciclo de leitura | `status`    | `want_to_read` · `reading` · `read` · `owned`   |
 * | Posse            | `ownership` | `physical` · `digital` · ausente (= não tenho)  |
 *
 * ## O que `status: "owned"` significa a partir daqui
 *
 * Deixou de significar "tenho" e passou a significar **"tenho, sem intenção de
 * leitura declarada"** — o 4º caso do produto ("só ter o livro, querendo ou não
 * ler"). É o mesmo valor na wire, de propósito: nenhum doc precisa ser
 * reescrito. A prateleira "Tenho" virou DERIVADA (`isBookOwned`), e por isso é
 * um superset — inclui os `reading`/`read`/`want_to_read` que o usuário também
 * possui. Consequência aceita: os 4 tiles do Perfil não somam mais o total da
 * estante.
 *
 * Invariante, garantido no choke point de escrita (`booksService`):
 * **`status === "owned"` ⟹ `ownership` não-nulo.** Um livro sem intenção de
 * leitura E sem posse não tem o que dizer — esse estado é "remover da estante",
 * que tem ação própria.
 *
 * ## Legado
 *
 * Docs criados antes deste campo não têm `ownership`. `ownershipOf` deriva a
 * posse de `status === "owned"` — sem isso, toda a estante "Tenho" de quem já
 * usava o app zeraria de uma vez. A derivação do FORMATO usa o único dado real
 * no doc (`editionFormat`), e não um chute: edição ebook → digital, resto →
 * físico (o rótulo antigo dessa estante era literalmente "estante física").
 *
 * A derivação é **lazy e permanente** — nunca assuma que `ownership` existe.
 * Quem escreve materializa o valor derivado (ver `ownershipForStatusChange`),
 * mas um doc que ninguém tocou continua respondendo certo por aqui.
 */

import type { BookStatus } from "../../../services/firebase/booksService";

/** Como o usuário tem o livro. Ausência do campo = não tem. */
export type BookOwnership = "physical" | "digital";

/**
 * O mínimo que `ownershipOf` precisa ler. Aceita `UserBook` inteiro, o
 * `NewUserBook` do fluxo de adicionar e o `data()` cru de um snapshot.
 */
export interface OwnershipSource {
  ownership?: BookOwnership | null;
  status?: BookStatus;
  editionFormat?: "physical" | "ebook" | null;
}

export function isBookOwnership(value: unknown): value is BookOwnership {
  return value === "physical" || value === "digital";
}

/**
 * Posse efetiva do livro — campo explícito quando existe, derivação de legado
 * quando não. `null` = não tem.
 */
export function ownershipOf(book: OwnershipSource | null | undefined): BookOwnership | null {
  if (!book) return null;
  if (isBookOwnership(book.ownership)) return book.ownership;
  // Legado: `status: "owned"` era o único jeito de declarar posse antes deste
  // eixo. Não havia distinção de formato — o único sinal real no doc é o
  // formato da EDIÇÃO.
  if (book.status === "owned") return book.editionFormat === "ebook" ? "digital" : "physical";
  return null;
}

export function isBookOwned(book: OwnershipSource | null | undefined): boolean {
  return ownershipOf(book) !== null;
}

// ---------------------------------------------------------------------------
// Rótulos
// ---------------------------------------------------------------------------

/**
 * "Tenho" e não "Na estante": `Estante` já nomeia a coleção inteira
 * (`Minha Estante`, `/shelf/*`), e o app usava as duas palavras pra duas
 * coisas diferentes — `STATUS_OPTIONS` dizia "Na Estante", os pills da
 * BookDetail diziam "Tenho". Uma palavra por conceito.
 */
export const OWNERSHIP_OPTIONS: { value: BookOwnership; label: string }[] = [
  { value: "physical", label: "Físico"  },
  { value: "digital",  label: "Digital" },
];

export function ownershipLabel(ownership: BookOwnership): string {
  return ownership === "digital" ? "Digital" : "Físico";
}

// ---------------------------------------------------------------------------
// Filtro das estantes
// ---------------------------------------------------------------------------

/**
 * Filtro de posse aplicado sobre uma estante.
 *
 * O conjunto de chips MUDA por estante, porque a pergunta muda:
 * - nas três de leitura, o que interessa é ter ou não ter (`owned`/`none`) —
 *   é o slice do presente: "quero ler" + "não tenho";
 * - na estante "Tenho", todo mundo tem, e a pergunta vira o formato.
 *
 * Ver `ownershipFilterOptions`.
 */
export type ShelfOwnershipFilter = "all" | "owned" | "none" | "physical" | "digital";

export function matchesOwnershipFilter(
  book: OwnershipSource,
  filter: ShelfOwnershipFilter,
): boolean {
  if (filter === "all") return true;
  const ownership = ownershipOf(book);
  if (filter === "owned") return ownership !== null;
  if (filter === "none") return ownership === null;
  return ownership === filter;
}

export interface OwnershipFilterOption {
  value: ShelfOwnershipFilter;
  label: string;
  /**
   * Recorte MAIS ESTREITO do item anterior — hoje só "Físico"/"Digital" sob
   * "Tenho". A folha de filtro indenta; quem renderiza plano pode ignorar.
   */
  nested?: boolean;
}

/**
 * Opções a exibir para uma estante, ou `[]` quando o controle não teria efeito.
 *
 * A lista sai dos livros presentes, não de uma constante: um controle que só
 * pode levar a "nenhum resultado" (estante onde todo mundo tem, ou ninguém
 * tem) é um botão morto ocupando a primeira dobra de quem acabou de chegar.
 * Por isso as opções são calculadas sobre a lista NÃO filtrada — senão elas
 * sumiriam no toque seguinte ao próprio filtro.
 *
 * ── Formato em TODAS as estantes (pedido do fundador, 02/08) ───────────────
 *
 * "Físico" e "Digital" existiam só em `/shelf/owned`. Fora dali a pergunta
 * "qual versão eu tenho deste livro" não tinha resposta — e ela vale nas
 * quatro: em "Lendo" é "estou lendo no papel ou no Kindle", em "Quero ler" é
 * o que decide se o presente é um livro ou um código de e-book.
 *
 * O eixo continua sendo UM (`ShelfOwnershipFilter` é valor único). Físico e
 * Digital são recortes de "Tenho", não uma segunda pergunta: quem não tem o
 * livro não tem formato nenhum. Por isso vêm indentados logo abaixo dele.
 *
 * ⚠️ Os dois formatos só aparecem quando os DOIS existem na estante. Com tudo
 * físico, "Físico" repetiria "Tenho" e "Digital" seria um botão que só leva a
 * lista vazia — a mesma regra que já governava a estante de posse.
 */
export function ownershipFilterOptions(
  books: OwnershipSource[],
  status: BookStatus,
): OwnershipFilterOption[] {
  const hasPhysical = books.some((b) => ownershipOf(b) === "physical");
  const hasDigital = books.some((b) => ownershipOf(b) === "digital");
  const hasOwned = hasPhysical || hasDigital;
  // Em `/shelf/owned` a estante é DERIVADA da posse: por construção não existe
  // livro sem posse aqui, e "Não tenho" não é uma opção que faça sentido.
  const hasMissing = status !== "owned" && books.some((b) => !isBookOwned(b));

  if (!hasOwned) return [];

  const options: OwnershipFilterOption[] = [{ value: "all", label: "Todos" }];
  if (hasMissing) options.push({ value: "owned", label: "Tenho" });
  if (hasPhysical && hasDigital) {
    options.push(
      { value: "physical", label: "Físico", nested: hasMissing },
      { value: "digital", label: "Digital", nested: hasMissing },
    );
  }
  if (hasMissing) options.push({ value: "none", label: "Não tenho" });

  // Só "Todos" = controle morto (estante inteira possuída, um formato só).
  return options.length > 1 ? options : [];
}
